import type { QueryClient } from "@tanstack/react-query";

// The entities BE's §05.1 actually names as stale-refetch targets (JAA-1058's
// resolved mapping — see that ticket's implementation note for how each FE
// spec label maps to its real hook/generateKey(...) entity, including why
// "System Power Profile" turned out to be the same hook as "System Component
// Comparison", not a separate one). An explicit allow-list, not a deny-list
// of systemStatus/facilityOverview — found via JAA-1095 live BE integration
// testing that the old deny-list caught every facility-scoped query
// incidentally, including structural/non-telemetry ones like
// useAvailableFacilityComponents (15-minute staleTime, not push-driven),
// refetching them needlessly on every event:stale.
export const STALE_REFETCH_ENTITIES = [
  "facilityKpis",
  "systemComponentComparison",
  "advancedEnergyAnalysis",
  "facilityAiInsights",
];

// Server emits event:stale unthrottled — debouncing/throttling it is
// explicitly a frontend responsibility (§05.1). ≤once per 30s per query key,
// per the FE spec — since a single invalidateQueries call here always
// invalidates every scoped key together (event:stale carries no per-entity
// scope to target), throttling the call itself achieves that bound for every
// affected key uniformly.
export const STALE_INVALIDATION_THROTTLE_MS = 30_000;

/**
 * Builds a handler for a facility's event:stale pushes, scoped to that
 * facility and matching only the named STALE_REFETCH_ENTITIES. Repeated calls
 * within STALE_INVALIDATION_THROTTLE_MS collapse to at most one
 * invalidateQueries call — leading-edge immediately, plus a single trailing
 * call if any additional stale event arrived during the throttle window, so
 * a burst never gets fully dropped.
 */
export function createStaleInvalidationHandler(
  queryClient: QueryClient,
  facilityId: number,
): () => void {
  let lastInvalidatedAt = 0;
  let trailingTimer: ReturnType<typeof setTimeout> | null = null;

  const invalidate = () => {
    lastInvalidatedAt = Date.now();
    queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[1] === facilityId &&
        STALE_REFETCH_ENTITIES.includes(query.queryKey[0] as string),
    });
  };

  return function handleStaleEvent() {
    const elapsed = Date.now() - lastInvalidatedAt;

    if (elapsed >= STALE_INVALIDATION_THROTTLE_MS) {
      invalidate();
      return;
    }

    if (trailingTimer) return; // a trailing call is already scheduled

    trailingTimer = setTimeout(() => {
      trailingTimer = null;
      invalidate();
    }, STALE_INVALIDATION_THROTTLE_MS - elapsed);
  };
}
